import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { useLocale } from '../../context/LocaleContext';
import { SelectedCarbonOffsetProjectT } from '../../pages/CarbonOffsetsPage/hooks/useCarbonOffsets';
import { SelectedSustainableActionsT } from '../../pages/SustainableActionsPage/hooks/useSustainableActions';
import Dropdown from '../Dropdown/Dropdown';
import EditInput from '../EditInput/EditInput';
import { InfoIcon } from '../Icon/Icon';
import Price from '../Price/Price';
import Tooltip from '../Tooltip/Tooltip';
import {
  DetailsList,
  EmptyMessageStyled,
  EmptyWrapperStyled,
  PriceWrapperStyled,
  PurchaseButtonStyled,
  RowStyled,
  SummaryItemsListStyled,
  TitleStyled,
  TotalSummaryRowStyled,
} from './SidebarDetails.styled';

type SidebarDetailsPropsT = {
  selectedSustainableActions?: SelectedSustainableActionsT[];
  selectedCarbonOffsetProjects?: SelectedCarbonOffsetProjectT[];
  children?: React.ReactNode;
  isEmptyCartShown?: boolean;
  isPurchaseButtonHidden?: boolean;
  isEditableSustainableActionsTotalCharge?: boolean;
  onClickPurchase?: () => void;
  onDeleteSustainableAction?: (id: number) => void;
  onDeleteCarbonOffset?: (id: number) => void;
  onChangeTotal?: (total: number) => void;
  onChangeSelectedSustainableActions?: (
    id: number,
    updatedAction: SelectedSustainableActionsT
  ) => void;
};

const renderSummaryItem = (
  {
    name,
    totalCharge,
  }: {
    name: string;
    totalCharge: string | number;
  },
  index: number
) => {
  return (
    <li key={index}>
      <span>{name}</span>
      <PriceWrapperStyled>
        <Price amount={totalCharge} />
      </PriceWrapperStyled>
    </li>
  );
};

const SidebarDetails: React.FC<SidebarDetailsPropsT> = ({
  selectedSustainableActions = [],
  selectedCarbonOffsetProjects = [],
  children,
  isPurchaseButtonHidden = false,
  isEditableSustainableActionsTotalCharge = false,
  onClickPurchase,
  onDeleteSustainableAction,
  onDeleteCarbonOffset,
  onChangeTotal,
  onChangeSelectedSustainableActions = () => null,
}) => {
  const { selectedLocale } = useLocale();
  const commonQty = selectedSustainableActions.length + selectedCarbonOffsetProjects.length;
  const hidePurchaseButton = !commonQty || isPurchaseButtonHidden;
  const isSummaryVisible = commonQty > 1;
  const sustainableActionsTotal = selectedSustainableActions.reduce(
    (acc, { totalCharge }) => +totalCharge + acc,
    0
  );
  const carbonOffsetTotal = selectedCarbonOffsetProjects.reduce(
    (acc, { totalCharge }) => +totalCharge + acc,
    0
  );
  const totalAmount = sustainableActionsTotal + carbonOffsetTotal;
  const isPurchaseDisabled = !totalAmount;

  const handleOnDeleteSustainableAction = (id: number) => {
    if (onDeleteSustainableAction) {
      onDeleteSustainableAction(id);
    }
  };

  const handleOnDeleteCarbonOffset = (id: number) => {
    if (onDeleteCarbonOffset) {
      onDeleteCarbonOffset(id);
    }
  };

  useEffect(() => {
    // rise up the total amount value to use it in Total price section on payment details page
    if (onChangeTotal) {
      onChangeTotal(totalAmount);
    }
  }, [totalAmount]);

  return (
    <>
      {selectedSustainableActions.map(
        ({ name, id, pricePerUnit, country, type, totalCharge, qty }, index) => (
          <Dropdown
            key={id}
            isOpen
            label={<TitleStyled>{name[selectedLocale]}</TitleStyled>}
            isDeleteHide={!onDeleteSustainableAction}
            onDelete={() => handleOnDeleteSustainableAction(id)}
          >
            <DetailsList>
              <li>
                <FormattedMessage id="foundItem.cost" defaultMessage="Unit Cost" />:{' '}
                <Price amount={pricePerUnit} />
              </li>
              <li>
                <FormattedMessage id="foundItem.type" defaultMessage="Type" />:{' '}
                <b>{type[selectedLocale]}</b>
              </li>
              <li>
                <FormattedMessage id="foundItem.country" defaultMessage="Country" />:{' '}
                <b>{country[selectedLocale]}</b>
              </li>
              <li>
                <FormattedMessage id="foundItem.numberOfUnits" defaultMessage="Number of units" />:{' '}
                <b> {qty}</b>
              </li>
              <li>
                <FormattedMessage id="foundItem.totalCharge" defaultMessage="Total Charge" />:{' '}
                {isEditableSustainableActionsTotalCharge ? (
                  <EditInput
                    min={Number(pricePerUnit) * qty}
                    label={
                      <b>
                        <Price amount={totalCharge} />
                      </b>
                    }
                    onChange={val => {
                      onChangeSelectedSustainableActions(id, {
                        ...selectedSustainableActions[index],
                        totalCharge: val,
                      });
                    }}
                  />
                ) : (
                  <b>
                    <Price amount={totalCharge} />
                  </b>
                )}
              </li>
            </DetailsList>
          </Dropdown>
        )
      )}

      {selectedCarbonOffsetProjects.map(({ name, id, basePrice, totalCharge, selectedQty }) => (
        <Dropdown
          key={id}
          isOpen
          isDeleteHide={!onDeleteCarbonOffset}
          label={<TitleStyled>{name[selectedLocale]}</TitleStyled>}
          onDelete={() => handleOnDeleteCarbonOffset(id)}
        >
          <DetailsList>
            <li>
              <FormattedMessage id="foundItem.priceTonne" defaultMessage="Price/tonne" />:{' '}
              <Price amount={basePrice} />
            </li>
            <li>
              <FormattedMessage id="foundItem.numberOfTonnes" defaultMessage="Number of tonnes" />:{' '}
              <b>{selectedQty}</b>
            </li>

            <li>
              <FormattedMessage id="foundItem.totalCharge" defaultMessage="Total Charge" />:{' '}
              <b>
                <Price amount={totalCharge} />
              </b>
            </li>
          </DetailsList>
        </Dropdown>
      ))}

      {!commonQty && (
        <Dropdown
          isOpen
          isClickable={false}
          isDeleteHide={true}
          label={
            <TitleStyled level={3}>
              <FormattedMessage id="common.summary" defaultMessage="Summary" />
            </TitleStyled>
          }
        >
          <EmptyWrapperStyled>
            <div>
              <img src="/images/icons/empty-cart.svg" alt="empty cart" />
            </div>

            <EmptyMessageStyled>
              <FormattedMessage
                id="emptyCart.text"
                defaultMessage="You have not made a selection yet."
              />
            </EmptyMessageStyled>
          </EmptyWrapperStyled>
        </Dropdown>
      )}

      {isSummaryVisible && (
        <Dropdown
          isOpen
          isDeleteHide
          isClickable={false}
          label={
            <TitleStyled level={3}>
              <FormattedMessage id="common.summary" defaultMessage="Summary" />
            </TitleStyled>
          }
        >
          <div>
            {!!selectedSustainableActions.length && (
              <RowStyled>
                <TitleStyled>
                  <FormattedMessage
                    id="sustainableActions.title"
                    defaultMessage="Sustainable Actions"
                  />
                </TitleStyled>

                <SummaryItemsListStyled>
                  {selectedSustainableActions.map(({ name, totalCharge }, index) =>
                    renderSummaryItem({ name: name[selectedLocale], totalCharge }, index)
                  )}
                </SummaryItemsListStyled>
              </RowStyled>
            )}

            {!!selectedCarbonOffsetProjects.length && (
              <RowStyled>
                <TitleStyled>
                  <FormattedMessage
                    id="offset.project.title"
                    defaultMessage="Carbon Offset Project"
                  />
                </TitleStyled>

                <SummaryItemsListStyled>
                  {selectedCarbonOffsetProjects.map(({ name, totalCharge }, index) =>
                    renderSummaryItem({ name: name[selectedLocale], totalCharge }, index)
                  )}
                </SummaryItemsListStyled>
              </RowStyled>
            )}

            <TotalSummaryRowStyled>
              <Tooltip element={<InfoIcon />}>
                <FormattedMessage
                  id="totalPrice.warning"
                  defaultMessage="This amount is not subject to refund."
                />
              </Tooltip>{' '}
              <strong>
                <FormattedMessage id="common.total" defaultMessage="Total" />:{' '}
              </strong>
              <Price amount={totalAmount} />
            </TotalSummaryRowStyled>
          </div>
        </Dropdown>
      )}

      {!hidePurchaseButton && (
        <PurchaseButtonStyled
          isInverted
          isFullWidth
          onClick={onClickPurchase}
          disabled={isPurchaseDisabled}
        >
          <FormattedMessage id="common.purchase" defaultMessage="Purchase" />
        </PurchaseButtonStyled>
      )}

      {children}
    </>
  );
};

export default SidebarDetails;
